import { ICategory, IGlossary, ITerm } from "@evennt-tech/api-v2-core";
import { IGetPublicInfoShowRes } from "@evennt-tech/api-v2-external";
import { useEffect, useMemo } from "react";
import useCurrentPublicShow from "hooks/useCurrentPublicShow";
import { useLocalisedLanguageCode } from "hooks/translation";
import { useGlossaries } from "store/glossary/glossary.store";
import { shallow } from "zustand/shallow";

type UseGlossaryArgs = {
  showId?: string;
  selectedCategoryId?: string;

  buildTypeId?: string;
  isReady?: boolean;
  localised?: boolean;
};

export type UserGlossaryRes = {
  glossary: IGlossary | null;
  isLoadingGlossary: boolean;

  categories: ICategory[];
  terms: ITerm[];
  allTerms: ITerm[];

  publicShow: IGetPublicInfoShowRes | null;
  isLoadingPublicShow: boolean;
};

function useGlossary({
  showId,
  buildTypeId,
  selectedCategoryId,
  isReady = true,
  localised = false,
}: UseGlossaryArgs): UserGlossaryRes {
  const {
    fetchGlossaryInShow,
    fetchCategoriesInShow,
    fetchTermsInShow,
    autoCatchGlossary,
    setSearchByBuildTypeId,
  } = useGlossaries((state) => ({
    fetchGlossaryInShow: state.fetch,
    fetchCategoriesInShow: state.fetchCategories,
    fetchTermsInShow: state.fetchTerms,
    autoCatchGlossary: state.autoCatch,
    setSearchByBuildTypeId: state.setSearchByBuildTypeId,
  }));

  // Shared react-query cache — same key every GlossaryTerm / RichText
  // mount uses, so the page fires ONE `GET /shows/public/:id` total
  // instead of a per-consumer fetch.
  const [publicShowData, isLoadingPublicShow] = useCurrentPublicShow({
    showId,
    enabled: !!showId && isReady,
  });
  const publicShow: IGetPublicInfoShowRes | null = publicShowData ?? null;

  const languageCode = useLocalisedLanguageCode(localised);

  useEffect(() => {
    if (!showId || !isReady) {
      return;
    }

    if (buildTypeId) {
      setSearchByBuildTypeId(showId, buildTypeId);
    }

    const load = async () => {
      await autoCatchGlossary(showId);
      await fetchGlossaryInShow(showId);
      await fetchCategoriesInShow(showId, false, languageCode);
      await fetchTermsInShow(showId, false, languageCode);
    };

    load();
  }, [showId, buildTypeId, isReady, languageCode]);

  const glossary = useGlossaries((state) => {
    if (!showId) {
      return null;
    }
    return state.glossaries[showId] ?? null;
  });

  const categories = useGlossaries((state) => {
    if (!showId) return [];

    return state.categories[showId] ?? [];
  });

  const terms = useGlossaries(({ terms }) => {
    if (!showId) {
      return [];
    }
    const showTerms = terms[showId];
    return showTerms ?? [];
  });

  const termsByCategory = useMemo(() => {
    if (!selectedCategoryId) {
      return terms;
    }
    return terms.filter((t) => t.categoryId === selectedCategoryId);
  }, [selectedCategoryId, terms]);

  const allTerms = useGlossaries((state) => {
    if (!showId) {
      return [];
    }

    return state.terms[showId] ?? [];
  });

  const isLoading = useGlossaries(
    (state) =>
      state.isLoadingGlossary ||
      state.isLoadingCategories ||
      state.isLoadingTerms
  );

  return {
    glossary,
    isLoadingGlossary: isLoading,

    categories,
    terms: termsByCategory,
    allTerms,

    publicShow,
    isLoadingPublicShow,
  };
}

export default useGlossary;
